// Duplicate parts (same number with / without the dealer code "007F/", spaces, dashes): merge them into one.
// The stock adds up, every job, order, count and stock record moves to the part you keep, and the other number is kept as an "other number".
import { useMemo, useRef, useState } from 'react';
import { api, useApi, dollars, mayI, type Row } from '../lib';
import { PartSuggest, rankParts, useSuggestKeys } from '../partsuggest';
import { Button, Modal, Panel, useToast, confirmAction } from '../ui';

export async function mergeParts(keep: Row, dup: Row) {
  return api<{ stock: number }>(`/api/parts/${keep.id}/merge`, { body: { from_id: dup.id } });
}
const sure = (keep: Row, dup: Row) => confirmAction(`Merge ${dup.sku} into ${keep.sku}?\n\n• ${dup.sku} disappears from the list\n• its ${dup.stock} on the shelf are added (${keep.stock} + ${dup.stock} = ${Number(keep.stock) + Number(dup.stock)})\n• its history, jobs and orders move to ${keep.sku}\n• "${dup.sku}" is kept as an other number, so scans and invoices still find it`);

/** Edit part → "Merge a duplicate into this part". */
export function MergeInto({ part, onDone }: { part: Row; onDone: () => void }) {
  const [open, setOpen] = useState(false);
  const toast = useToast();
  const catalog = useApi<Row[]>(open ? '/api/parts' : null, [open]);
  const [q, setQ] = useState('');
  const ref = useRef<HTMLInputElement>(null);
  const { items, total } = useMemo(() => rankParts((catalog.data || []).filter(p => p.id !== part.id), q), [catalog.data, q, part.id]);
  const keys = useSuggestKeys(items.length, q.trim().length >= 2);
  const go = async (dup: Row) => {
    if (!sure(part, dup)) return;
    try { const r = await mergeParts(part, dup); toast(`Merged — ${part.sku} has ${r.stock} on the shelf now`); setOpen(false); onDone(); }
    catch (e) { toast((e as Error).message, 'err'); }
  };
  return <>
    <button type="button" className="linkish small full" onClick={() => setOpen(true)}>⧉ Same part twice? Merge a duplicate into this one…</button>
    {open && <Modal title={`Merge a duplicate into ${part.sku}`} onClose={() => setOpen(false)}>
      <div className="form-body">
        <p className="form-note full">Find the other copy (e.g. <b>SP 350CB03X</b> and <b>007F/SP 350CB03X</b>). <b>{part.sku}</b> stays; the other one's stock and history move here.</p>
        <label className="field full"><span>The duplicate</span>
          <input ref={ref} value={q} onChange={e => setQ(e.target.value)} placeholder="Part # or name" autoFocus autoComplete="off"
            onKeyDown={e => { const i = keys.onKey(e); if (i !== null && items[i]) void go(items[i]); }} /></label>
        <PartSuggest anchor={ref} open={q.trim().length >= 2} q={q} items={items} total={total} hi={keys.hi} onHover={keys.setHi} showPrice={false} onPick={p => void go(p)} />
      </div>
    </Modal>}
  </>;
}

/** Leaks & review → "Possible duplicates": each group, pick the one to keep. */
export function Duplicates() {
  const { data, reload } = useApi<Row[][]>('/api/parts/duplicates');
  const toast = useToast();
  const [busy, setBusy] = useState('');
  if (!data?.length) return null;
  const keepThis = async (keep: Row, group: Row[]) => {
    const others = group.filter(p => p.id !== keep.id);
    if (!confirmAction(`Keep ${keep.sku} and merge ${others.map(o => o.sku).join(', ')} into it?\nStock adds up (${group.reduce((a, p) => a + Number(p.stock), 0)} on the shelf); history, jobs and orders move to ${keep.sku}; the other numbers are kept as other numbers.`)) return;
    setBusy(keep.id);
    try { for (const o of others) await mergeParts(keep, o); toast(`Merged into ${keep.sku}`); reload(); }
    catch (e) { toast((e as Error).message, 'err'); } finally { setBusy(''); }
  };
  return (
    <Panel title={`Possible duplicates (${data.length})`}>
      <p className="muted small pad-x">The same part # twice — usually one with the dealer code from Fullbay ("007F/…") and one without. Pick the one to keep; the others merge into it.</p>
      <div className="dup-list">{data.map(g => (
        <div key={g[0].id} className="dup-group">{g.map((p, i) => (
          <div key={p.id} className="dup-row">
            <span className="grow"><b>{p.sku}</b> <span className="muted">{p.name}</span><small className="muted block">{p.supplier || 'no vendor'}{p.bin ? ` · bin ${p.bin}` : ''} · {p.stock} on shelf{mayI('costs') ? ` · cost ${dollars(p.cost)}` : ''} · price {dollars(p.price)} · {p.moves} stock records{i === 0 ? ' · most used' : ''}</small></span>
            <Button className="small" kind={i === 0 ? 'primary' : undefined} busy={busy === p.id} disabled={!!busy} onClick={() => void keepThis(p, g)}>Keep this one</Button>
          </div>))}
        </div>))}</div>
    </Panel>
  );
}
